import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const ThemeContext = createContext();

export const UI_COLOR_TOKENS = [
  {
    group: 'Brand',
    tokens: [
      { cssVar: '--ut-orange', label: 'Primary accent', description: 'Buttons, active tabs, focus rings' },
      { cssVar: '--ut-orange-light', label: 'Accent highlight', description: 'Hover states and glow accents' },
      { cssVar: '--ut-orange-dark', label: 'Accent depth', description: 'Button gradients and active depth' },
      { cssVar: '--ut-blue', label: 'Link blue', description: 'Links and secondary accents' },
      { cssVar: '--ut-blue-light', label: 'Link hover', description: 'Hover links and selected graph nodes' },
      { cssVar: '--ut-blue-dark', label: 'Blue depth', description: 'Pressed and dark blue states' },
      { cssVar: '--ut-gray', label: 'Smokey gray', description: 'Neutral brand gray' },
      { cssVar: '--ut-light-gray', label: 'Light neutral', description: 'Light-mode page and panel fills' },
    ]
  },
  {
    group: 'Surfaces',
    tokens: [
      { cssVar: '--bg-primary', label: 'Page background', description: 'Main application background' },
      { cssVar: '--bg-secondary', label: 'Panel background', description: 'Cards, tabs, sidebars, panels' },
      { cssVar: '--bg-tertiary', label: 'Raised surface', description: 'Inputs, nested controls, secondary cards' },
      { cssVar: '--chat-bg', label: 'Chat background', description: 'Conversation workspace background' },
      { cssVar: '--chat-bubble-bot', label: 'Assistant bubble', description: 'Assistant message bubble fill' },
    ]
  },
  {
    group: 'Text',
    tokens: [
      { cssVar: '--text-primary', label: 'Primary text', description: 'Headings and important content' },
      { cssVar: '--text-secondary', label: 'Secondary text', description: 'Descriptions, metadata, labels' },
      { cssVar: '--text-muted', label: 'Muted text', description: 'Hints, timestamps, disabled-ish copy' },
    ]
  },
  {
    group: 'Borders',
    tokens: [
      { cssVar: '--border-color', label: 'Default border', description: 'Cards, inputs, dividers' },
      { cssVar: '--border-light', label: 'Soft border', description: 'Subtle dividers and quiet outlines' },
    ]
  },
  {
    group: 'Status',
    tokens: [
      { cssVar: '--success', label: 'Success', description: 'Success messages and healthy status' },
      { cssVar: '--warning', label: 'Warning', description: 'Warnings and review badges' },
      { cssVar: '--error', label: 'Error', description: 'Errors and destructive states' },
      { cssVar: '--info', label: 'Info', description: 'Informational accents' },
    ]
  },
];

export const DEFAULT_THEME_COLORS = {
  light: {
    '--ut-orange': '#FF8200',
    '--ut-orange-light': '#FF9933',
    '--ut-orange-dark': '#E67300',
    '--ut-blue': '#005A8B',
    '--ut-blue-light': '#0077B6',
    '--ut-blue-dark': '#004466',
    '--ut-gray': '#58595B',
    '--ut-light-gray': '#F7F8FA',
    '--success': '#10B981',
    '--warning': '#F59E0B',
    '--error': '#EF4444',
    '--info': '#3B82F6',
    '--bg-primary': '#F7F8FA',
    '--bg-secondary': '#FFFFFF',
    '--bg-tertiary': '#F0F2F5',
    '--text-primary': '#1A1D21',
    '--text-secondary': '#5E6670',
    '--text-muted': '#8B95A5',
    '--border-color': '#D0D5DB',
    '--border-light': '#E9EBEF',
    '--chat-bg': '#FAFBFC',
    '--chat-bubble-bot': '#FFFFFF',
  },
  dark: {
    '--ut-orange': '#FF8C00',
    '--ut-orange-light': '#FFA333',
    '--ut-orange-dark': '#E67300',
    '--ut-blue': '#58A6FF',
    '--ut-blue-light': '#79C0FF',
    '--ut-blue-dark': '#388BFD',
    '--ut-gray': '#73767A',
    '--ut-light-gray': '#E0E4EA',
    '--success': '#34D399',
    '--warning': '#FBBF24',
    '--error': '#F87171',
    '--info': '#60A5FA',
    '--bg-primary': '#10151D',
    '--bg-secondary': '#171D26',
    '--bg-tertiary': '#232B36',
    '--text-primary': '#F4F7FA',
    '--text-secondary': '#D2DAE4',
    '--text-muted': '#B4BEC9',
    '--border-color': '#596575',
    '--border-light': '#374151',
    '--chat-bg': '#10151D',
    '--chat-bubble-bot': '#202833',
  }
};

export const ACCESSIBLE_PRESETS = {
  dark: {
    name: 'High-readability dark',
    colors: {
      '--bg-primary': '#10151D',
      '--bg-secondary': '#171D26',
      '--bg-tertiary': '#232B36',
      '--chat-bg': '#10151D',
      '--chat-bubble-bot': '#202833',
      '--text-primary': '#F8FAFC',
      '--text-secondary': '#DDE5EE',
      '--text-muted': '#C2CBD6',
      '--border-color': '#647184',
      '--border-light': '#424C5A',
      '--ut-orange': '#FF9D2E',
      '--ut-orange-light': '#FFB35C',
      '--ut-orange-dark': '#F47C00',
      '--ut-blue': '#7BC2FF',
      '--ut-blue-light': '#9BD2FF',
      '--ut-blue-dark': '#58A6FF',
    }
  },
  light: {
    name: 'Balanced light',
    colors: {
      '--bg-primary': '#F7F8FA',
      '--bg-secondary': '#FFFFFF',
      '--bg-tertiary': '#EEF1F5',
      '--chat-bg': '#FAFBFC',
      '--chat-bubble-bot': '#FFFFFF',
      '--text-primary': '#161A1F',
      '--text-secondary': '#46505C',
      '--text-muted': '#687486',
      '--border-color': '#C6CDD6',
      '--border-light': '#E4E8EE',
    }
  }
};

const COLOR_STORAGE_KEY = 'ppa_ui_color_overrides_v1';
const PREF_STORAGE_KEY = 'ppa_ui_preferences_v1';

const DEFAULT_UI_PREFERENCES = {
  fontScale: 1,
  density: 'comfortable',
  highContrastText: true,
  reduceBackgroundEffects: false,
};

const allColorVars = UI_COLOR_TOKENS.flatMap(group => group.tokens.map(token => token.cssVar));
const isHexColor = (value) => /^#[0-9A-Fa-f]{6}$/.test(value || '');

const readJson = (key, fallback) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch {
    return fallback;
  }
};

const sanitizeColorOverrides = (candidate) => {
  const next = { light: {}, dark: {} };
  ['light', 'dark'].forEach(themeKey => {
    const source = candidate?.[themeKey] || {};
    allColorVars.forEach(cssVar => {
      if (isHexColor(source[cssVar])) {
        next[themeKey][cssVar] = source[cssVar].toUpperCase();
      }
    });
  });
  return next;
};

const sanitizePreferences = (candidate) => ({
  fontScale: Math.min(1.25, Math.max(0.9, Number(candidate?.fontScale) || DEFAULT_UI_PREFERENCES.fontScale)),
  density: ['compact', 'comfortable', 'spacious'].includes(candidate?.density) ? candidate.density : DEFAULT_UI_PREFERENCES.density,
  highContrastText: typeof candidate?.highContrastText === 'boolean' ? candidate.highContrastText : DEFAULT_UI_PREFERENCES.highContrastText,
  reduceBackgroundEffects: typeof candidate?.reduceBackgroundEffects === 'boolean' ? candidate.reduceBackgroundEffects : DEFAULT_UI_PREFERENCES.reduceBackgroundEffects,
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const storedTheme = localStorage.getItem('theme');
    return storedTheme ? storedTheme === 'dark' : true;
  });

  const [colorOverrides, setColorOverrides] = useState(() => (
    sanitizeColorOverrides(readJson(COLOR_STORAGE_KEY, { light: {}, dark: {} }))
  ));

  const [uiPreferences, setUiPreferencesState] = useState(() => (
    sanitizePreferences(readJson(PREF_STORAGE_KEY, DEFAULT_UI_PREFERENCES))
  ));

  useEffect(() => {
    if (isDarkMode) {
      document.body.setAttribute('data-theme', 'dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.body.removeAttribute('data-theme');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  useEffect(() => {
    const themeKey = isDarkMode ? 'dark' : 'light';
    const activeOverrides = colorOverrides[themeKey] || {};

    allColorVars.forEach(cssVar => {
      const value = activeOverrides[cssVar];
      if (isHexColor(value)) {
        document.documentElement.style.setProperty(cssVar, value);
      } else {
        document.documentElement.style.removeProperty(cssVar);
      }
    });
  }, [isDarkMode, colorOverrides]);

  useEffect(() => {
    document.documentElement.style.setProperty('--ui-font-scale', uiPreferences.fontScale);
    document.body.dataset.uiDensity = uiPreferences.density;
    document.body.classList.toggle('ui-high-contrast', uiPreferences.highContrastText);
    document.body.classList.toggle('ui-reduce-background-effects', uiPreferences.reduceBackgroundEffects);
  }, [uiPreferences]);

  useEffect(() => {
    localStorage.setItem(COLOR_STORAGE_KEY, JSON.stringify(colorOverrides));
  }, [colorOverrides]);

  useEffect(() => {
    localStorage.setItem(PREF_STORAGE_KEY, JSON.stringify(uiPreferences));
  }, [uiPreferences]);

  const setDarkMode = useCallback((enabled) => setIsDarkMode(Boolean(enabled)), []);
  const toggleDarkMode = useCallback(() => setIsDarkMode(prev => !prev), []);

  const setColorToken = useCallback((themeKey, cssVar, value) => {
    if (!['light', 'dark'].includes(themeKey) || !allColorVars.includes(cssVar) || !isHexColor(value)) return false;
    setColorOverrides(prev => ({
      ...prev,
      [themeKey]: {
        ...(prev[themeKey] || {}),
        [cssVar]: value.toUpperCase(),
      }
    }));
    return true;
  }, []);

  const resetColorToken = useCallback((themeKey, cssVar) => {
    setColorOverrides(prev => {
      const nextTheme = { ...(prev[themeKey] || {}) };
      delete nextTheme[cssVar];
      return { ...prev, [themeKey]: nextTheme };
    });
  }, []);

  const resetThemeColors = useCallback((themeKey) => {
    setColorOverrides(prev => ({ ...prev, [themeKey]: {} }));
  }, []);

  const resetAllColors = useCallback(() => {
    setColorOverrides({ light: {}, dark: {} });
  }, []);

  const applyColorPreset = useCallback((themeKey, preset = ACCESSIBLE_PRESETS[themeKey]) => {
    if (!preset?.colors) return;
    setColorOverrides(prev => ({
      ...prev,
      [themeKey]: {
        ...(prev[themeKey] || {}),
        ...preset.colors,
      }
    }));
  }, []);

  const setUiPreference = useCallback((key, value) => {
    setUiPreferencesState(prev => sanitizePreferences({ ...prev, [key]: value }));
  }, []);

  const resetUiPreferences = useCallback(() => {
    setUiPreferencesState(DEFAULT_UI_PREFERENCES);
  }, []);

  const getEffectiveColor = useCallback((themeKey, cssVar) => {
    return colorOverrides?.[themeKey]?.[cssVar] || DEFAULT_THEME_COLORS?.[themeKey]?.[cssVar] || '#000000';
  }, [colorOverrides]);

  const exportUiSettings = useCallback(() => ({
    version: 1,
    colors: colorOverrides,
    preferences: uiPreferences,
  }), [colorOverrides, uiPreferences]);

  const importUiSettings = useCallback((settings) => {
    if (!settings || typeof settings !== 'object') return false;
    setColorOverrides(sanitizeColorOverrides(settings.colors || settings));
    if (settings.preferences) {
      setUiPreferencesState(sanitizePreferences(settings.preferences));
    }
    return true;
  }, []);

  const value = useMemo(() => ({
    isDarkMode,
    themeKey: isDarkMode ? 'dark' : 'light',
    toggleDarkMode,
    setDarkMode,
    colorOverrides,
    uiPreferences,
    setColorToken,
    resetColorToken,
    resetThemeColors,
    resetAllColors,
    applyColorPreset,
    setUiPreference,
    resetUiPreferences,
    getEffectiveColor,
    exportUiSettings,
    importUiSettings,
  }), [
    isDarkMode,
    toggleDarkMode,
    setDarkMode,
    colorOverrides,
    uiPreferences,
    setColorToken,
    resetColorToken,
    resetThemeColors,
    resetAllColors,
    applyColorPreset,
    setUiPreference,
    resetUiPreferences,
    getEffectiveColor,
    exportUiSettings,
    importUiSettings,
  ]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
